import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    parsePaging,
    parseQuery,
    parseSorting,
} from '../../utils/queryString';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Spinner from '../Spinner';
import Paginator from '../Paginator';
import Error from '../errors/Error';
import CashLimitsList from './CashLimitsList';
import Search from '../Search';
import {
    ALL_CASH_LIMITS,
    ON_CASH_LIMIT_CHANGED,
} from '../../graphql/cashLimits';
import UploadButton from '../buttons/UploadButton';
import Constants from '../../utils/Constants';
import {
    hasPrivilege,
    Privileged,
} from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import CashLimitsUploadResponse from './CashLimitsUploadResponse';
import InfoPanel from '../InfoPanel';
import { transformClearingHouseBlockers } from '../blockers/ClearingHouseBlockers';
import { getValueMapping } from './CashLimitRow';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import { showInfoNotification } from '../../actions/notifications';
import useAuthentication from '../../hooks/useAuthentication';
import AS2ConnectionIndicator from '../indicators/AS2ConnectionIndicator';
import './_styles_/CashLimits.css';

function CashLimits() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'mainId',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const query = parseQuery(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        query,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
        previousData,
    } = useExtendedQuery(ALL_CASH_LIMITS, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onSearch = (newQuery) => setRefetchProps({
        ...refetchProps,
        query: newQuery,
        page: 0,
    });
    const onUpload = () => refetch(refetchProps);
    useExtendedSubscription(ON_CASH_LIMIT_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'cashLimitChanged',
        onChangeCallback: () => {
            if (hasPrivilege(userPrivileges, [Privileges.CASH_LIMIT_EDIT])) {
                const notification = t('cash-limits.notification.cash-limits-modified-thirdparty');
                dispatch(showInfoNotification(notification));
            }
        },
    });

    const responseDataComponent = (responseData) => (
        <CashLimitsUploadResponse responseData={responseData} />
    );

    let result;
    let cashLimitProps = null;
    let searchMatches = 0;
    if (loading) {
        result = <Spinner className="cash-limits-loading" />;
    } else if (error) {
        result = <Error className="cash-limits-error" error={error} />;
    } else {
        const searchResult = data === undefined ? previousData : data;
        searchMatches = searchResult.allCashLimits.cashLimitList.totalCount;
        cashLimitProps = {
            availableUploadClearingHouses: searchResult.allCashLimits.availableUploadClearingHouses,
        };
        result = (
            <Paginator
                totalCount={searchResult.allCashLimits.cashLimitList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <CashLimitsList
                    cashLimits={searchResult.allCashLimits.cashLimitList.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <div className="page-title-with-indicator">
                <h4><FontAwesomeIcon icon="funnel-dollar" />&nbsp;{t('title.cash-limits')}</h4>
                <AS2ConnectionIndicator />
            </div>
            <Privileged sufficientPrivileges={[Privileges.CASH_LIMIT_EDIT]}>
                <div className="search-and-filter">
                    <UploadButton
                        label={t('cash-limits.upload-cash-limits')}
                        className="button-bordered"
                        uploadPath={Constants.UPLOAD_CASH_LIMITS_PATH}
                        accept=".xml"
                        onUpload={onUpload}
                        responseComp={responseDataComponent}
                        disabled={
                            !cashLimitProps
                                || cashLimitProps.availableUploadClearingHouses.every(
                                    (availableUploadClearingHouse) => availableUploadClearingHouse.uploadBlockers?.length > 0,
                                )
                        }
                    />
                </div>
                {cashLimitProps && cashLimitProps.availableUploadClearingHouses.map((availableUploadClearingHouse) => availableUploadClearingHouse.uploadBlockers?.map((uploadBlocker) => (
                    <InfoPanel
                        key={uploadBlocker}
                        message={transformClearingHouseBlockers(t, uploadBlocker, availableUploadClearingHouse.name, availableUploadClearingHouse.code)}
                    />
                )))}
            </Privileged>
            <div className="search-and-filter">
                <Search
                    placeholder={t('cash-limits.placeholder.search')}
                    query={refetchProps.query}
                    onSearch={onSearch}
                    matches={searchMatches}
                />
            </div>
            {result}
        </>
    );
}

export default CashLimits;
